 'use client';

 import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { FormationDetailProps } from '@/types/formation';
import { Button } from '@/components/ui/button';
import { siteConfig } from '@/config/site.config';
import { trackEvent } from '@/lib/analytics';

function SectionShell({
  id,
  title,
  subtitle,
  tone,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  tone?: 'default' | 'soft';
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className={
        tone === 'soft'
          ? 'scroll-mt-28 rounded-2xl border border-white/10 bg-primary-950/50 p-6 backdrop-blur md:p-8'
          : 'scroll-mt-28 rounded-2xl border border-white/10 bg-primary-950/35 p-6 backdrop-blur md:p-8'
      }
    >
      <div className="flex items-start gap-4">
        <div className="mt-1 h-10 w-1.5 rounded-full bg-accent-400" aria-hidden="true" />
        <div className="min-w-0">
          <h2 className="text-2xl font-bold tracking-tight text-white">{title}</h2>
          {subtitle ? <p className="mt-2 text-white/75">{subtitle}</p> : null}
        </div>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function toText(value: string | string[] | undefined) {
  if (!value) {
    return 'Aucun prerequis specifique.';
  }

  return Array.isArray(value) ? value.join(' ') : value;
}

export default function FormationDetailPage({
  formation
}: {
  formation: FormationDetailProps
}) {
  const [isCompactNav, setIsCompactNav] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setIsCompactNav(window.scrollY > 140);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    trackEvent('view_formation', {
      formation_slug: formation.slug,
      formation_title: formation.title,
      formation_category: formation.category,
    });
  }, [formation.category, formation.slug, formation.title]);

  const faqObjections = [
    {
      question: 'Cette formation est-elle obligatoire ?'
      ,
      answer: 'Cela depend de votre activite, de vos risques et de vos obligations reglementaires. Nous vous aidons a clarifier le cadre applicable et a definir le parcours le plus pertinent.',
    },
    {
      question: 'Combien de temps la certification est-elle valable ?'
      ,
      answer: formation.maintainingKnowledge || 'La duree de validite et les recyclages varient selon la certification et votre secteur. Contactez-nous pour confirmer la periodicite et planifier la mise a jour.',
    },
    {
      question: 'Un financement est-il possible (OPCO) ?'
      ,
      answer: 'Oui, nous vous guidons dans la constitution du dossier (devis, programme, objectifs, pieces demandées) et adaptons la proposition selon votre branche et vos contraintes.',
    },
  ];

  const prereqText = toText(formation.prerequisites);

  const quickLinks = useMemo(
    () => [
      { href: '#valeur', label: 'Valeur' },
      { href: '#preuves', label: 'Preuves' },
      { href: '#programme', label: 'Programme' },
      { href: '#acces', label: 'Modalites' },
      { href: '#faq', label: 'FAQ' },
    ],
    []
  );

  const heroKicker = useMemo(() => {
    const duration = formation.duration?.toLowerCase().includes('heure')
      ? formation.duration
      : `en ${formation.duration}`;
    return `Des reflexes terrain ${duration}`;
  }, [formation.duration]);

  return (
    <div className="bg-[#050B18] text-white">
      <div className="mx-auto w-full max-w-7xl px-4 pb-28 pt-10 sm:px-6 lg:px-8">
      <div className="pt-2">
        <Link href="/formations" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 transition-colors hover:text-accent-200">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour au catalogue
        </Link>
      </div>

      <header
        className={
          'relative mt-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#06122F] via-[#050B18] to-[#0A1638] p-6 shadow-[0_20px_80px_-50px_rgba(0,0,0,0.8)] md:p-10'
        }
      >
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-accent-400/15 blur-3xl" />
          <div className="absolute -bottom-28 -right-20 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-400/60 to-transparent" />
        </div>

        <div
          className={
            'relative grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center ' +
            (isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2') +
            ' motion-safe:transition-all motion-safe:duration-700'
          }
        >
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/80">
                {formation.category}
              </span>
              {formation.reference ? (
                <span className="inline-flex rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold tracking-wide text-white/70">
                  Ref. {formation.reference}
                </span>
              ) : null}
            </div>

            <h1 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-white md:text-6xl">
              {heroKicker}
            </h1>
            <p className="mt-4 text-lg font-semibold text-white/85 md:text-2xl">
              {formation.title}
            </p>
            {formation.habilitation ? (
              <p className="mt-3 inline-flex rounded-full border border-accent-300/40 bg-accent-300/10 px-4 py-1.5 text-sm font-semibold text-accent-200">
                {formation.habilitation}
              </p>
            ) : null}
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
              {formation.description}
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Duree</p>
                <p className="mt-2 text-sm font-semibold text-white">{formation.duration}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Certification</p>
                <p className="mt-2 text-sm font-semibold text-white">{formation.certification || 'Attestation'}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Public</p>
                <p className="mt-2 text-sm font-semibold text-white">{formation.targetAudience || 'Professionnels'}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Prerequis</p>
                <p className="mt-2 text-sm font-semibold text-white">{prereqText}</p>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild variant="cta" className="group h-12 px-6">
                <Link href={`/contact?formation=${formation.slug}`} className="inline-flex items-center justify-center gap-2">
                  Demander un devis
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l6 6-6 6" />
                  </svg>
                </Link>
              </Button>
              <Button asChild variant="outline-accent" className="group h-12 px-6">
                <Link href="#programme" className="inline-flex items-center justify-center gap-2">
                  Voir le programme
                  <span className="text-accent-300 transition-transform duration-300 group-hover:translate-y-0.5">↓</span>
                </Link>
              </Button>
              <Button asChild variant="ghost-accent" className="h-12 px-6">
                <a
                  href={`tel:${siteConfig.contact.phoneHref}`}
                  data-track-event="click_phone"
                  data-track-location="formation_hero"
                  data-track-label={formation.slug}
                >
                  Appeler
                </a>
              </Button>
            </div>

            <p className="mt-6 text-sm text-white/65">
              Organisation sous 10 a 15 jours en moyenne apres validation du besoin. Intra, inter ou sur mesure.
            </p>
          </div>

          <div className="relative">
            <div className="relative h-64 overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-sm md:h-[420px]">
              <Image
                src={formation.imageSrc || '/images/formations/default.webp'}
                alt={formation.title}
                fill
                className="object-cover opacity-95 transition-transform duration-700 hover:scale-[1.02]"
                sizes="(max-width: 1024px) 100vw, 45vw"
                priority
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#050B18]/70 via-transparent to-transparent" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-white/70">
              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">
                Certifie Qualiopi
              </span>
              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">
                Formateurs terrain
              </span>
              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">
                Adaptation a vos risques
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="sticky top-16 z-30 -mx-4 mt-6 border-y border-white/10 bg-[#050B18]/80 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <nav aria-label="Navigation de la page">
          <div className={"flex flex-wrap items-center gap-2 " + (isCompactNav ? 'justify-between' : '')}>
            <div className="flex flex-wrap gap-2">
              {quickLinks.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={
                    'inline-flex items-center rounded-full border border-white/10 bg-white/5 font-semibold text-white/80 transition-all hover:border-accent-400/40 hover:text-white ' +
                    (isCompactNav ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm')
                  }
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className={"hidden items-center gap-2 sm:flex " + (isCompactNav ? 'opacity-100' : 'opacity-0 pointer-events-none')}>
              <Button asChild size="sm" variant="cta" className="h-9">
                <Link href={`/contact?formation=${formation.slug}`}>Devis</Link>
              </Button>
              <Button asChild size="sm" variant="cta-outline" className="h-9">
                <a
                  href={`tel:${siteConfig.contact.phoneHref}`}
                  data-track-event="click_phone"
                  data-track-location="formation_sticky_nav"
                  data-track-label={formation.slug}
                >
                  Appeler
                </a>
              </Button>
            </div>
          </div>
        </nav>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="space-y-8">
          <SectionShell
            id="valeur"
            title="Pourquoi cette formation ?"
            subtitle="Une formation efficace doit etre utile sur le terrain, conforme, et facilement deployable en entreprise."
            tone="soft"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Reflexes immediats</p>
                <p className="mt-2 text-sm text-white/70">Des gestes et procedures applicables des le lendemain, avec mises en situation.</p>
              </div>
              <div className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Reduction du risque</p>
                <p className="mt-2 text-sm text-white/70">Limitez l&apos;impact des incidents et renforcez la culture prevention de vos equipes.</p>
              </div>
              <div className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Organisation simple</p>
                <p className="mt-2 text-sm text-white/70">Formats intra/inter et planification adaptee a vos contraintes d&apos;exploitation.</p>
              </div>
            </div>
          </SectionShell>

          {formation.objectives && formation.objectives.length > 0 && (
            <SectionShell id="objectifs" title="Objectifs" tone="default">
              <ul className="space-y-2">
                {formation.objectives.map((objective, index) => (
                  <li key={index} className="flex items-start gap-3 text-white/80">
                    <span className="mt-1 text-accent-300">•</span>
                    <span>{objective}</span>
                  </li>
                ))}
              </ul>
            </SectionShell>
          )}

          <SectionShell
            id="preuves"
            title="Preuves & credibilite"
            subtitle="Des engagements concrets pour une formation serieuse, utile et facile a deployer."
            tone="soft"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Organisme certifie</p>
                <p className="mt-2 text-sm text-white/70">Certification Qualiopi et process de suivi pour vos obligations.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Formateurs terrain</p>
                <p className="mt-2 text-sm text-white/70">Mises en situation, cas concrets et adaptation a votre contexte metier.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Accompagnement OPCO</p>
                <p className="mt-2 text-sm text-white/70">Devis, programme, pieces: on vous aide a monter le dossier rapidement.</p>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-accent-400/20 bg-white/5 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">Un besoin urgent ou une question avant de valider ?</p>
                  <p className="mt-1 text-sm text-white/70">Appelez-nous au {siteConfig.contact.phoneDisplay} ou demandez un devis.</p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button asChild variant="cta-outline" className="h-11">
                    <a
                      href={`tel:${siteConfig.contact.phoneHref}`}
                      data-track-event="click_phone"
                      data-track-location="formation_proof_cta"
                      data-track-label={formation.slug}
                    >
                      Appeler
                    </a>
                  </Button>
                  <Button asChild variant="cta" className="h-11">
                    <Link href={`/contact?formation=${formation.slug}`}>Demander un devis</Link>
                  </Button>
                </div>
              </div>
            </div>
          </SectionShell>

          <SectionShell
            id="programme"
            title="Programme structure"
            subtitle="Une progression claire, avec alternance theorie et pratique."
            tone="soft"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-600">
                Cliquez sur un module pour voir le detail.
              </div>
              <Button asChild variant="outline-primary" className="h-10">
                <Link href={`/contact?formation=${formation.slug}`}>Recevoir le programme</Link>
              </Button>
            </div>

            <div className="mt-5 space-y-4">
              {formation.program?.map((section, index) => (
                <details
                  key={index}
                  className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:border-accent-400/25 hover:bg-white/10"
                  open={index === 0}
                >
                  <summary className="flex cursor-pointer list-none items-start gap-4">
                    <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-400 text-xs font-bold text-[#050B18]">
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-white">{section.title}</span>
                      <span className="mt-1 block text-sm text-white/60 group-open:hidden">
                        {section.items.length} points - afficher le detail
                      </span>
                    </span>
                    <span className="mt-1 text-xs font-semibold text-white/60 group-open:hidden">+</span>
                    <span className="mt-1 text-xs font-semibold text-white/60 hidden group-open:inline">-</span>
                  </summary>

                  <div className="mt-4 overflow-hidden">
                    <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-out group-open:grid-rows-[1fr] motion-reduce:transition-none">
                      <div className="min-h-0">
                        <div className="border-t border-white/10 pt-4">
                          <ul className="space-y-2">
                      {section.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-3 text-white/80">
                              <span className="mt-1 text-accent-300">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </SectionShell>

          {(formation.pedagogicalMethods || formation.evaluationMethods) && (
            <SectionShell id="modalites" title="Modalites pedagogiques" tone="default">
              <div className="grid gap-4 md:grid-cols-2">
                {formation.pedagogicalMethods && (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="text-sm font-semibold text-white">Methodes</p>
                    <ul className="mt-3 space-y-2">
                      {formation.pedagogicalMethods.map((method, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm text-white/80">
                          <span className="mt-1 text-accent-300">•</span>
                          <span>{method}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {formation.evaluationMethods && (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="text-sm font-semibold text-white">Evaluation</p>
                    <ul className="mt-3 space-y-2">
                      {formation.evaluationMethods.map((method, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm text-white/80">
                          <span className="mt-1 text-accent-300">•</span>
                          <span>{method}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </SectionShell>
          )}

          <SectionShell id="acces" title="Modalites & financement" tone="soft">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Tarif</p>
                <p className="mt-2 text-sm text-white/70">{formation.price || 'Sur devis selon effectif et lieu.'}</p>
              </div>
              <div className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Prise en charge</p>
                <p className="mt-2 text-sm text-white/70">Accompagnement possible pour les dossiers OPCO.</p>
              </div>
              <div className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Formats</p>
                <p className="mt-2 text-sm text-white/70">Intra-entreprise, inter-entreprises et sur mesure.</p>
              </div>
              <div className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent-400/30 hover:bg-white/10">
                <p className="text-sm font-semibold text-white">Mise en place</p>
                <p className="mt-2 text-sm text-white/70">10 a 15 jours en moyenne apres validation du besoin.</p>
              </div>
            </div>

            {formation.registrationProcess && (
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-sm font-semibold text-white">Inscription</p>
                <ul className="mt-3 space-y-2">
                  {formation.registrationProcess.map((process, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-white/80">
                      <span className="mt-1 text-accent-300">•</span>
                      <span>{process}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </SectionShell>

          {formation.accessibilityCriteria && (
            <SectionShell id="accessibilite" title="Accessibilite" tone="default">
              <p className="text-white/80">{formation.accessibilityCriteria}</p>
            </SectionShell>
          )}

          {formation.formatterInfo && (
            <SectionShell id="intervenant" title="Intervenant" tone="soft">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
                <p className="text-lg font-semibold text-white">{formation.formatterInfo.name}</p>
                <div className="mt-3 grid gap-2 text-sm text-white/75 sm:grid-cols-2">
                  <a
                    href={`tel:${formation.formatterInfo.phone.replace(/\s/g, '')}`}
                    className="hover:text-accent-200 hover:underline"
                    data-track-event="click_phone"
                    data-track-location="formation_formatter"
                    data-track-label={formation.slug}
                  >
                    {formation.formatterInfo.phone}
                  </a>
                  <a
                    href={`mailto:${formation.formatterInfo.email}`}
                    className="hover:text-accent-200 hover:underline"
                    data-track-event="click_email"
                    data-track-location="formation_formatter"
                    data-track-label={formation.slug}
                  >
                    {formation.formatterInfo.email}
                  </a>
                </div>
              </div>
            </SectionShell>
          )}

          <SectionShell id="faq" title="FAQ" subtitle="Les questions les plus frequentes avant de valider une session." tone="default">
            <div className="space-y-3">
              {faqObjections.map((faq) => (
                <details key={faq.question} className="group rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm transition-all hover:border-accent-400/25 hover:bg-white/10">
                  <summary className="cursor-pointer list-none font-semibold text-white/90 group-hover:text-white">
                    {faq.question}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-white/75">{faq.answer}</p>
                </details>
              ))}
            </div>
          </SectionShell>

          <section className="rounded-2xl border border-accent-400/25 bg-gradient-to-r from-accent-400/10 via-white/5 to-transparent p-6 md:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-white">Prêt a organiser votre session ?</h2>
                <p className="mt-2 max-w-2xl text-white/75">Recevez une proposition adaptee a votre effectif, votre niveau et vos contraintes logistiques.</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild variant="cta" className="group h-12">
                  <Link href={`/contact?formation=${formation.slug}`}>Demander un devis</Link>
                </Button>
                <Button asChild variant="cta-outline" className="h-12">
                  <Link href="/planning-inscription">Voir les sessions</Link>
                </Button>
              </div>
            </div>
          </section>
        </div>

        <aside className="relative">
          <div className="sticky top-28 rounded-2xl border border-white/10 bg-primary-950/50 p-6 shadow-sm backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Synthese</p>
            <h2 className="mt-3 text-lg font-bold tracking-tight text-white">{formation.title}</h2>
            <div className="mt-5 space-y-2 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
              <p><span className="font-semibold text-white">Duree:</span> {formation.duration}</p>
              <p><span className="font-semibold text-white">Public:</span> {formation.targetAudience || 'Professionnels'}</p>
              <p><span className="font-semibold text-white">Prerequis:</span> {prereqText}</p>
              <p><span className="font-semibold text-white">Certification:</span> {formation.certification || 'Attestation'}</p>
              {formation.price ? (
                <p><span className="font-semibold text-white">Tarif:</span> {formation.price}</p>
              ) : null}
            </div>

            <div className="mt-5 space-y-3">
              <Button asChild variant="cta" className="w-full">
                <Link href={`/contact?formation=${formation.slug}`}>Demander un devis</Link>
              </Button>
              <Button asChild variant="outline-accent" className="w-full">
                <Link href="#faq">Voir la FAQ</Link>
              </Button>
            </div>

            <div className="mt-6 rounded-2xl border border-accent-400/20 bg-white/5 p-5">
              <p className="text-sm font-semibold text-white">Besoin d&apos;un parcours sur mesure ?</p>
              <p className="mt-2 text-sm text-white/75">On vous recommande le bon niveau et le bon rythme (intra/inter, multi-sites, contraintes metier).</p>
              <div className="mt-3">
                <Button asChild variant="cta-outline" className="w-full">
                  <Link href="/contact">Echanger avec un conseiller</Link>
                </Button>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#050B18]/80 backdrop-blur lg:hidden">
        <div className="mx-auto flex w-full max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
          <Button asChild variant="cta" className="group h-12 flex-1">
            <Link href={`/contact?formation=${formation.slug}`} className="inline-flex items-center justify-center gap-2">
              Demander un devis
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l6 6-6 6" />
              </svg>
            </Link>
          </Button>
          <Button asChild variant="cta-outline" className="h-12 flex-1">
            <a
              href={`tel:${siteConfig.contact.phoneHref}`}
              data-track-event="click_phone"
              data-track-location="formation_mobile_sticky"
              data-track-label={formation.slug}
            >
              Appeler
            </a>
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}
